'use client'
import {SanityImageType, VideoOrImageType} from '@local/types'
import {
	Button,
	Locale,
	Pastil,
	SanityImage,
	SmallText,
	VideoLoop,
} from '@local/ui/components'
import {cx} from '@local/utils/cx'
import {ForwardedRef, forwardRef, useRef, useState} from 'react'
import {formatPrice} from '@local/utils/currency'
import {ProductGridCardComingSoon} from './ProductGridCardComingSoon'
import {useMatchTouch} from '@local/hooks'
import RatingIcon from '@local/assets/svgs/icon-rating.svg'

export interface ProductPreviewProps {
	store: {
		tags: string;
		productTag?: string;
		variants: Array<{ type: string }>;
		createdAt: string;
		priceRange: {
			minVariantPrice: string;
			maxVariantPrice: string;
		};
	};

	slug: string;
	slugPrefix: 'experience' | 'merch';
	excerpt?: string;
	location?: string;
	datePeriod?: string;
	productType?: string;
}

type TagTypes =
  | 'kaer-choice'
  | 'coming-soon'
  | 'fully-booked'
  | 'last-spots'
  | 'previous-experience';

export interface ProductGridCardProps {
	className?: string;
	media: VideoOrImageType;
	image?: SanityImageType;
	slug: string;
	item: ProductPreviewProps;
	index: number;
	id: string;
	tag?: TagTypes;
	keywords?: string;
	type?: string;
	disabled?: boolean;
	reviewAuthor?: string;
	reviewText?: string;
	itemCount: number;
}

export const ProductGridCard = forwardRef(
	(
		{
			id,
			tag,
			slug,
			item,
			index,
			media,
			disabled,
			className,
			reviewAuthor,
			reviewText,
			itemCount,
			keywords,
		}: ProductGridCardProps,
		ref: ForwardedRef<HTMLLIElement>,
	) => {
		const rowCount = Math.ceil(itemCount / 5)
		const isLastRow = index >= (rowCount - 1) * 5
		const isSmallLastRow =
      isLastRow && (rowCount % 2 !== 0 ? index % 2 === 0 : index % 2 !== 0)
		const isSmall = index % 2 !== 0
		const isSmallMobile = isSmall && index < 2
		const isSmallDesktop = (isSmall && index < 5) || isSmallLastRow
		const isTranslatedDesktop =
      ((index - 1) % 5 === 0 || (index - 3) % 5 === 0) && index > 5
		const isComingSoon = tag === 'coming-soon'
		const videoRef = useRef<HTMLVideoElement>(null)

		const [isHover, setIsHover] = useState(false)
		const handleMouseEnter = () => {
			if (videoRef.current) {
				videoRef.current.restart()
				videoRef.current.play()
			}
			setIsHover(true)
		}
		const handleMouseLeave = () => {
			if (videoRef.current) {
				videoRef.current.pause()
			}
			setIsHover(false)
		}
		const isTouch = useMatchTouch()

		return (
			<li
				data-id={id}
				ref={ref}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				className={cx(
					'relative span-w-6 lg:w-full overflow-clip group rounded-lg list-none',
					isSmallMobile ? 'h-240' : 'h-300',
					isSmallDesktop ? 'lg:h-300' : 'lg:h-400',
					isTranslatedDesktop && 'lg:-translate-y-100',
					isSmall && index > 1 && 'lg-max:-translate-y-60',
					className,
				)}>
				{
					reviewAuthor
						? (
							<div
								className={cx(
									'group/btn outline-none cursor-pointer bg-white-off  rounded-lg w-full h-full overflow-hidden !relative !block',
								)}>
								<div className='flip-card bg-white-off w-full h-full'>
									<div className='flip-card-inner h-full w-full'>

										<div className='flip-card-front w-full h-full bg-grey-3'>
											<div
												className={cx(
													'!absolute inset-0 flex text-center flex-col items-center justify-center text-white bg-black/25 px-12 lg:px-30',
													'transition-[backdrop-filter,background-color] transform-gpu duration-700 ease-quad-out',
													'backdrop-blur-[2px] bg-black/40',
													'group-focus-visible/btn:backdrop-blur-[2px] group-focus-visible/btn:bg-black/40',
												)}>

												<SmallText textColor='white'>{reviewText}</SmallText>
												<div className='flex items-center mt-15 justify-center w-full gap-2'>
													<h3 className='font-medium font-sans w-1/2 uppercase whitespace-nowrap  text-12 text-center leading-none tracking-normal'>
														{reviewAuthor}
													</h3>
													<div className='h-12 w-fit'>
														<RatingIcon className='fill-white h-12' />
													</div>
												</div>
											</div>

										</div>
										<div className='flip-card-back w-full h-full'>
											<Button
												href={`/${item.slugPrefix}/${item.slug}`}
												disabled={disabled}
												className={cx(
													disabled && 'hidden',
													'group/btn outline-none w-full h-full !relative !block',
												)}>
												<SanityImage
													image={media?.video?.poster || media?.image}
													width={200}
													height={isSmall && index < 2 ? 240 : 300}
													mobileWidth={200}
													mobileOnly
													fill
													priority={index < 2}
													className={cx(
														'span-w-7 object-cover',
														isSmall && index < 2 ? 'h-240' : 'h-300',
													)} />
												<SanityImage
													image={media?.video?.poster || media?.image}
													width={268}
													height={isSmall && index < 5 ? 300 : 400}
													desktopWidth={268}
													desktopOnly
													fill
													priority={index < 5}
													className={cx(
														'lg:w-full object-cover',
														isSmall && index < 5 ? 'lg:h-300' : 'lg:h-400',
														'transition-transform duration-700 ease-[cubic-bezier(.35,0,.19,1)]',
														'scale-110 group-hover:scale-100 group-focus-visible/btn:scale-100',
													)} />
												{media?.video?.srcs && (
													<VideoLoop
														srcs={media.video.srcs}
														poster={media.video.poster}
														posterMobileWidth={200}
														posterDesktopWidth={268}
														ratio={isSmall && index < 5 ? 268 / 300 : 268 / 400}
														ratioMobile={isSmall && index < 2 ? 200 / 240 : 200 / 300}
														priority={index < 5 && !isTouch}
														preload={isTouch ? 'none' : 'metadata'}
														playing={isHover}
														ref={videoRef}
														fill
														className={cx(
															'absolute inset-0',
															'span-w-7 lg:w-full object-cover [&_video]:object-cover',
															isSmall && index < 2 ? 'h-240' : 'h-300',
															isSmall && index < 5 ? 'lg:h-300' : 'lg:h-400',
															'transition-[transform,opacity] duration-700 ease-[cubic-bezier(.35,0,.19,1)]',
															'scale-110 group-hover:scale-100 group-focus-visible/btn:scale-100',
															'opacity-0 group-hover:opacity-100 group-focus-visible/btn:scale-100',
														)} />
												)}
												<div
													className={cx(
														'!absolute inset-0 flex flex-col items-center justify-center text-white bg-black/25 px-12 lg:px-30',
														'transition-[backdrop-filter,background-color] transform-gpu duration-700 ease-quad-out',
														'group-hover:backdrop-blur-[2px] group-hover:bg-black/40',
														'group-focus-visible/btn:backdrop-blur-[2px] group-focus-visible/btn:bg-black/40',
													)}>
													<h3 className='font-serif capitalize text-16 lg:text-20 leading-snug mb-6'>
														{item?.title}
													</h3>
													{item.slugPrefix === 'experience' && item.location && (
														<SmallText textColor='white'>
															<span>
																<Locale>Location</Locale>:{' '}
															</span>
															<span>{item.location}</span>
														</SmallText>
													)}

													{item.slugPrefix === 'experience' && item.datePeriod?.trim() && (
														<SmallText textColor='white'>{item.datePeriod}</SmallText>
													)}

												</div>
											</Button>
										</div>
									</div>
								</div>
							</div>
						)
						: (
							<Button
								href={slug}
								disabled={disabled}
								className={cx(
									disabled && 'hidden',
									'group/btn outline-none w-full h-full !relative !block',
								)}>
								<SanityImage
									image={media?.video?.poster || media?.image}
									width={200}
									height={isSmall && index < 2 ? 240 : 300}
									mobileWidth={200}
									mobileOnly
									fill
									priority={index < 2}
									className={cx(
										'span-w-7 object-cover',
										isSmall && index < 2 ? 'h-240' : 'h-300',
									)} />
								<SanityImage
									image={media?.video?.poster || media?.image}
									width={268}
									height={isSmall && index < 5 ? 300 : 400}
									desktopWidth={268}
									desktopOnly
									fill
									priority={index < 5}
									className={cx(
										'lg:w-full object-cover',
										isSmall && index < 5 ? 'lg:h-300' : 'lg:h-400',
										'transition-transform duration-700 ease-[cubic-bezier(.35,0,.19,1)]',
										'scale-110 group-hover:scale-100 group-focus-visible/btn:scale-100',
									)} />
								{media?.video?.srcs && (
									<VideoLoop
										srcs={media.video.srcs}
										poster={media.video.poster}
										posterMobileWidth={200}
										posterDesktopWidth={268}
										ratio={isSmall && index < 5 ? 268 / 300 : 268 / 400}
										ratioMobile={isSmall && index < 2 ? 200 / 240 : 200 / 300}
										priority={index < 5 && !isTouch}
										preload={isTouch ? 'none' : 'metadata'}
										playing={isHover}
										ref={videoRef}
										fill
										className={cx(
											'absolute inset-0',
											'span-w-7 lg:w-full object-cover [&_video]:object-cover',
											isSmall && index < 2 ? 'h-240' : 'h-300',
											isSmall && index < 5 ? 'lg:h-300' : 'lg:h-400',
											'transition-[transform,opacity] duration-700 ease-[cubic-bezier(.35,0,.19,1)]',
											'scale-110 group-hover:scale-100 group-focus-visible/btn:scale-100',
											'opacity-0 group-hover:opacity-100 group-focus-visible/btn:scale-100',
										)} />
								)}
								<div
									className={cx(
										'!absolute inset-0 flex flex-col items-center justify-center text-white bg-black/25 px-12 lg:px-30',
										'transition-[backdrop-filter,background-color] transform-gpu duration-700 ease-quad-out',
										'group-hover:backdrop-blur-[2px] group-hover:bg-black/40',
										'group-focus-visible/btn:backdrop-blur-[2px] group-focus-visible/btn:bg-black/40',
									)}>
									<h3 className='font-serif capitalize text-16 lg:text-20 leading-snug mb-6'>
										{item.title}
									</h3>
									{item.slugPrefix === 'experience' && item.location && (
										<SmallText textColor='white'>
											<span>
												<Locale>Location</Locale>:{' '}
											</span>
											<span>{item.location}</span>
										</SmallText>
									)}
									{item.slugPrefix === 'merch' && (
										<SmallText textColor='white'>
											<span>{item.productType}</span>
										</SmallText>
									)}
									{item.slugPrefix === 'experience' && item.datePeriod?.trim() && (
										<SmallText textColor='white'>{item.datePeriod}</SmallText>
									)}
									{isComingSoon && (
										<ProductGridCardComingSoon retreat={item.title} />
									)}
									{tag === 'kaer-choice' && (
										<Pastil className='absolute top-20'>
											<Locale>Kaer Choice</Locale>
										</Pastil>
									)}
									{tag === 'fully-booked' && (
										<Pastil className='absolute top-20'>
											<Locale>Fully Booked</Locale>
										</Pastil>
									)}
									{tag === 'last-spots' && (
										<Pastil className='absolute top-20'>
											<Locale>Last Spots</Locale>
										</Pastil>
									)}
									{tag === 'previous-experience' && (
										<Pastil variant='grey' className='absolute top-20'>
											<Locale>Previous Experience</Locale>
										</Pastil>
									)}
									<div className='absolute bottom-20 left-1/2 transform -translate-x-1/2'>
										<SmallText textColor='white'>
											<span>
												{keywords}
											</span>
										</SmallText>
									</div>
									{item?.excerpt && item?.store?.priceRange?.minVariantPrice
										? (
											<SmallText textColor='white' className='absolute bottom-20'>
												{item.excerpt +
                    formatPrice(item.store.priceRange.minVariantPrice, 'nl-NL', undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0},
                    ).replace(/\s/g, '')}
											</SmallText>
										)
										: (
											<></>
										)}
								</div>
							</Button>
						)
				}
			</li>
		)
	},
)
